import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, MapPin } from 'lucide-react';
import { BRAND_NAME, arrivalSteps, distances, mapsEmbedUrl, mapsUrl } from '../data/site';
import { Reveal } from './Reveal';
import { SectionLabel } from './SectionLabel';
export function LocationSection() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapVisible, setMapVisible] = useState(false);
  useEffect(() => {
    const node = mapRef.current;
    if (!node || mapVisible) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setMapVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '300px'
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [mapVisible]);
  return <section id="location" className="w-full bg-sand-light py-24 sm:py-28 lg:py-36" data-id="element-191" data-mp="6rcloudtbybi">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12" data-id="element-192" data-mp="2qkcu28urvfr">
        <Reveal className="max-w-2xl" data-id="element-193" data-mp="2mr3xwhepvwif">
          <SectionLabel data-id="element-194" data-mp="2ms6pkrjv62ze">Location</SectionLabel>
          <h2 className="mt-6 font-serif text-[clamp(2rem,4.5vw,3.5rem)] font-light leading-[1.08] text-forest-deep" data-id="element-195" data-mp="2rpf7cr13o504">
            Perfectly located in Taveuni
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:mt-20 lg:grid-cols-5" data-id="element-196" data-mp="31fhzyq4b7gxx">
          <Reveal className="lg:col-span-3" data-id="element-197" data-mp="31gt1ejaa8clv">
            <div ref={mapRef} className="relative h-[320px] overflow-hidden rounded-[1.75rem] bg-sand shadow-soft sm:h-[420px] lg:h-full lg:min-h-[520px]" data-id="element-198" data-mp="36f4bmv4hop4m">
              {mapVisible && <iframe src={mapsEmbedUrl} title={`Map showing ${BRAND_NAME} in Taveuni, Fiji`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 h-full w-full border-0" data-id="element-199" data-mp="3fgknz8ev71bh" />}

              <div className="pointer-events-none absolute bottom-4 left-4 right-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-200" data-mp="1q2fjdv27mrdr">
                <div className="pointer-events-auto inline-flex items-center gap-3 rounded-2xl bg-ivory/95 px-4 py-3 shadow-soft backdrop-blur-sm" data-id="element-201" data-mp="1dytki5vh2nj7">
                  <MapPin className="h-4 w-4 flex-shrink-0 text-gold" aria-hidden="true" data-id="element-202" data-mp="1rvqf8iepop5d" />
                  <span data-id="element-203" data-mp="20xpxfe1gpz9w">
                    <span className="block font-serif text-lg leading-none text-forest-deep" data-id="element-204" data-mp="20wyb6ttr9dwa">
                      Pelly Homestay
                    </span>
                    <span className="mt-1 block text-[10px] uppercase tracking-[0.16em] text-forest-deep/50" data-id="element-205" data-mp="2aou7bzca6txv">
                      Taveuni, Fiji
                    </span>
                  </span>
                </div>

                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="pointer-events-auto inline-flex items-center gap-2 self-start rounded-full bg-ivory/95 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-forest-deep shadow-soft transition-colors duration-300 hover:bg-ivory sm:self-auto" data-id="element-206" data-mp="2knjr5xnqmogr">
                  Open in Google Maps
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" data-id="element-207" data-mp="2ua4883xitwua" />
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2" data-id="element-208" data-mp="2fket14slfsb0">
            <div className="h-full rounded-[1.75rem] bg-ivory p-8 shadow-soft sm:p-10" data-id="element-209" data-mp="2kboh40yhunks">
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.16em] text-forest-deep" data-id="element-210" data-mp="26kibdn4mftpg">
                Getting around
              </h3>
              <ul className="mt-7 space-y-4" data-id="element-211" data-mp="2bkefrnh8dn5j">
                {distances.map(item => <li key={item.label} className="flex items-baseline justify-between gap-4 border-b border-sand-dark/40 pb-4 last:border-0" data-id="element-212" data-mp="1wnnch34frran">
                    <span className="text-[15px] text-forest-deep/75" data-id="element-213" data-mp="34v1dg0x3kfll">
                      {item.label}
                    </span>
                    <span className="font-serif text-lg text-ocean" data-id="element-214" data-mp="2zzcjqe7rdsx7">
                      {item.time}
                    </span>
                  </li>)}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="mt-20" data-id="element-215" data-mp="2l8va47kyzwp8">
          <Reveal data-id="element-216" data-mp="2l9540694yo07">
            <h3 className="font-serif text-[clamp(1.6rem,3vw,2.5rem)] font-light text-forest-deep" data-id="element-217" data-mp="2l69266u63f6h">
              How to get here
            </h3>
          </Reveal>

          <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-218" data-mp="2c8qc2o1o2anq">
            {arrivalSteps.map((step, i) => <Reveal as="li" key={step.step} delay={i * 0.08} data-id="element-219" data-mp="27af3egn3y673" data-mpi={i}>
                <div className="h-full rounded-[1.5rem] border border-sand-dark/50 bg-ivory p-7 transition-all duration-500 ease-calm hover:-translate-y-1 hover:shadow-soft" data-id="element-220" data-mp="agkchwe0l3pw">
                  <span className="font-serif text-[13px] tracking-[0.2em] text-gold" data-id="element-221" data-mp="d1059obxsvak">
                    {step.step}
                  </span>
                  <h4 className="mt-4 text-[15px] font-semibold text-forest-deep" data-id="element-222" data-mp="hyiezgxs3ejm">
                    {step.title}
                  </h4>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-forest-deep/60" data-id="element-223" data-mp="msw8fdsvrbln">
                    {step.description}
                  </p>
                </div>
              </Reveal>)}
          </ol>
        </div>
      </div>
    </section>;
}